/**
 * Landing v3 — the redesigned marketing landing (Figma qvKH4DGebnMCNK7SJ72iCm).
 *
 * Desktop is one continuous scroll-driven scene. Scroll offset (in
 * viewport-heights, `s`) is the single source of truth: the scattered hero
 * chips gather into the bottom-nav slots in proportion to `s`, the brand pill
 * hands over to the top bar, and the feature cards slide through as a
 * horizontal train — every transform/opacity is a pure function of scroll
 * (framer computed `useTransform`), so slow scrolling holds any
 * mid-transition frame with nothing snapping. Springs are only used for the
 * one-shot intro (logo pill → headline reveal → CTA rises → chips pop in and
 * float). Each chip + its badge live in ONE transformed group, so rotation,
 * drift and gathering can never desync.
 *
 * Below `lg` the scatter/gather choreography doesn't map to a narrow
 * viewport: phones and tablets in portrait get the same story rebuilt for
 * touch (`landing-phone.tsx`).
 */
import { useQueryClient } from '@tanstack/react-query';
import {
  animate,
  type AnimationPlaybackControls,
  motion,
  type MotionValue,
  useAnimationFrame,
  useInView,
  useMotionValue,
  useMotionValueEvent,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from 'framer-motion';
import {
  memo,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  FALLBACK_USER_COUNT,
  formatCompactMillions,
  useLiveUserCount,
} from '@/hooks/use-live-user-count';
import { cn } from '@/lib/utils';
import { tagLandingVersion } from '@/providers/posthog';
import { RTL_LANGS } from '@/seo/marketing-routes';

import { AgentChat } from './landing-agent-chat';
import {
  ChipFace,
  clamp01,
  easeInOutCubic,
  FeatureCardDeck,
  FeatureRail,
  FlashkaWordmark,
  GetStartedCta,
  GLASS_PILL_CLASS,
  HEADLINE_CLASS,
  HERO_CHIP_SIZE,
  kaCoverAt,
  type LandingScroll,
  LAYOUT_SPRING,
  LogoMark,
  PageLattice,
  RAIL_CHIP_SIZE,
  sectionAnchor,
  StoreRatingPills,
  TopBar,
  TrustedPill,
  useIsDesktop,
  useLandingCardArrival,
  useLandingSectionArrival,
  useScrollTravel,
} from './landing-chrome';
import { useDeckSnap } from './landing-deck-snap';
import { FaqSection } from './landing-faq';
import {
  INTRO_POP_ORDER,
  LANDING_FEATURES,
  LANDING_SPANS,
  LANDING_TIMELINE,
  type LandingFeature,
} from './landing-features';
import { LandingFooter } from './landing-footer';
import { FloatingGetApp } from './landing-get-app';
import { WandIcon } from './landing-icons';
import {
  DESK_KA,
  DESK_KA_HEADROOM,
  DeskKa,
  EASE_OUT,
  FloatingKa,
  KA_BOX,
  KA_FLOAT,
  KA_GLIDE,
  KA_SMALL,
  KA_SMALL_SCALE,
  KaSlot,
  setKaLanded,
  useKaInCard,
} from './landing-ka';
import { kaSlotAt } from './landing-ka-travel';
import { prefetchLandingLeaderboard } from './landing-leaderboard';
import { KA_LINES, KaWord, toWords, TriggeredLine } from './landing-lines';
import { WorldDotMap } from './landing-map';
import { PhoneLanding } from './landing-phone';
import { TikTokButton, VideoCarousel } from './landing-posts';
import { ReviewsMarquee } from './landing-reviews';
import { useStoreDevice } from './landing-store';
import { TilesSection } from './landing-tiles';

// ── Timed motion vocabulary ─────────────────────────────────────────────────

/** How early (in viewport-heights) a section claims Ka / fires its reveal. */
const TRIGGER_LEAD = 0.14;

/**
 * What Ka is doing right now. This is DISCRETE state, flipped once per
 * threshold crossing — not a scrubbed scroll mapping. Ka then animates to the
 * matching pose on its own timeline, so its entrance plays once and never
 * reverses or re-runs while you keep scrolling.
 */
type KaStage = { kind: 'corner' } | { kind: 'ka'; k: number };

function stageAt(s: number): KaStage {
  const { kaStarts } = LANDING_TIMELINE;
  for (let k = kaStarts.length - 1; k >= 0; k -= 1) {
    if (s >= kaStarts[k] - TRIGGER_LEAD && s < kaStarts[k] + LANDING_SPANS.ka) {
      return { kind: 'ka', k };
    }
  }
  return { kind: 'corner' };
}

const sameStage = (a: KaStage, b: KaStage) =>
  a.kind === b.kind && (a.kind !== 'ka' || b.kind !== 'ka' || a.k === b.k);

/**
 * One mid-page interstitial line (Figma 12:242 / 12:354 / 12:584): two centred
 * rows that stream in once when the interstitial is first reached (`fired`),
 * then simply fade with `active` as the next cluster takes the stage — no
 * re-streaming, no scroll-linked reversal.
 */
function KaLine({
  lineIndex,
  fired,
  active,
  reduceMotion,
}: {
  lineIndex: number;
  fired: boolean;
  active: boolean;
  reduceMotion: boolean;
}) {
  const { t } = useTranslation();
  const line = KA_LINES[lineIndex];
  const rows = line.rows.map((row) =>
    toWords(row.map((seg) => ({ text: t(seg.key), hl: seg.hl }))),
  );

  let wordIndex = 0;
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: active ? 1 : 0 }}
      transition={reduceMotion ? { duration: 0 } : { duration: 0.32, ease: 'easeOut' }}
      className="absolute inset-x-0 top-2 mx-auto w-[1030px] max-w-[85vw] text-center text-[44px] font-bold leading-[52px] text-secondary-800">
      {rows.map((row, ri) => (
        <span key={ri} className="block">
          {row.map((token, wi) => {
            const i = wordIndex;
            wordIndex += 1;
            return (
              <KaWord
                key={wi}
                text={token.word}
                hl={token.hl}
                leadingSpace={wi > 0 && !token.attach}
                index={i}
                play={fired}
                delay={0.45}
                reduceMotion={reduceMotion}
              />
            );
          })}
        </span>
      ))}
    </motion.div>
  );
}

// ── Below the runway: social proof, leaderboard, closing CTA ────────────────
//
// From the map onward the page scrolls NORMALLY. Each block is a centred
// `min-h-screen` section that fires ONCE when it scrolls into view
// (`useInView`, once). Ka is one fixed element (see `KaTravel`) that glides
// between the blocks' `KaSlot`s as the scroll passes through them.

/** The idle nudge (Figma 75:23556): Ka at 64px + its speech pill, bottom-centre. */
const NUDGE_IDLE_MS = 2000;
const NUDGE_SESSION_KEY = 'lv3-nudged';
const NUDGE_GROUP_W = 229;
const NUDGE_BOTTOM = 68;
const NUDGE_TOAST_GAP = 8;
const NUDGE_TOAST_RAISE = 12;

/**
 * One block below the runway. Plain flow — the page's vertical rhythm is set
 * by the stack that holds these (200px between blocks, Figma 48:20942), not
 * by viewport pagination. Fires once when 30% of it is in view. A block
 * without a `KaSlot` has Ka bottom-right while it is on screen.
 */
function Block({
  children,
  className,
}: {
  children: (arrived: boolean) => ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLElement>(null);
  const arrived = useInView(ref, { once: true, amount: 0.3 });
  return (
    <section
      ref={ref}
      className={cn('relative flex w-full flex-col items-center', className)}>
      {children(arrived)}
    </section>
  );
}

/**
 * Social proof: Ka, "+2M students study this way", the dot world map with the
 * store reviews riding across it (Figma 48:21594 / 48:21099). Ka→headline 13,
 * headline→map 24. Fires ONCE in view.
 *
 * The headline is a rounded-down "more than" figure, so an unknown `users`
 * simply reads the static floor — one word either way, and the word-by-word
 * reveal splits on spaces, which the compact form never contains.
 */
function ProofSection({
  reduceMotion,
  users,
}: {
  reduceMotion: boolean;
  users: number | null;
}) {
  const { t, i18n } = useTranslation();
  const total = formatCompactMillions(users ?? FALLBACK_USER_COUNT, i18n.language);
  return (
    <Block>
      {(arrived) => (
        <>
          <KaSlot id="proof" size={KA_SMALL} />
          <TriggeredLine
            segments={[
              { text: t('landingV3.social.count', { total }), hl: true },
              { text: t('landingV3.social.students') },
            ]}
            arrived={arrived}
            delay={0.1}
            reduceMotion={reduceMotion}
            className={cn('mt-[13px]', HEADLINE_CLASS)}
          />
          <div className="relative mt-6 flex w-full justify-center">
            <WorldDotMap arrived={arrived} reduceMotion={reduceMotion} />
            {/* The review cards ride across the map; their glass frosts the
                dots behind them. No transform on this band — see the note in
                landing-reviews about backdrop roots. */}
            <div className="absolute inset-x-0" style={{ top: `calc(50% - 150px)` }}>
              <ReviewsMarquee play={arrived} />
            </div>
          </div>
        </>
      )}
    </Block>
  );
}

/** The six product tiles (Figma 118:1538). Ka sits bottom-right here.
 *  Each tile times its own entrance (see TilesSection), not the block. */
function TilesBlock() {
  return <Block>{() => <TilesSection />}</Block>;
}

/**
 * "Thousands post about us everyday" + the video carousel + the TikTok button
 * (Figma 48:21705 / 48:21713 / 48:21717). Ka is centred for the headline and
 * parks bottom-right while the carousel is on screen. Gaps: Ka→headline 13,
 * headline→carousel 100, carousel→button 58.
 */
function PostsSection({ reduceMotion }: { reduceMotion: boolean }) {
  const { t } = useTranslation();
  return (
    <Block>
      {(arrived) => (
        <>
          <KaSlot id="posts" size={KA_SMALL} />
          <TriggeredLine
            segments={[
              { text: t('landingV3.posts.hl'), hl: true },
              { text: t('landingV3.posts.post') },
            ]}
            arrived={arrived}
            delay={0.1}
            reduceMotion={reduceMotion}
            className={cn('mt-[13px] max-w-[548px]', HEADLINE_CLASS)}
          />
          <div className="mt-[100px] w-full">
            <VideoCarousel play={arrived} />
          </div>
          <div className="mt-[58px]">
            <TikTokButton />
          </div>
        </>
      )}
    </Block>
  );
}

/**
 * The closing section (Figma 109:1432): Ka at his desk, "All of this with Ka,
 * your 24/7 tutor.", the Start Studying Now CTA and the two store pills. Gaps
 * 50 / 72 / 16. No badge — the master frame dropped it.
 */
function FinalKaSection({
  reduceMotion,
  deskShown,
  signUpVariant,
}: {
  reduceMotion: boolean;
  deskShown: MotionValue<number>;
  /** Secondary where the device has a store (its button is the primary). */
  signUpVariant: 'primary' | 'secondary';
}) {
  const { t } = useTranslation();
  const rise = (arrived: boolean, delay: number) => ({
    initial: { opacity: 0, y: 16 },
    animate: arrived ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 },
    transition: reduceMotion
      ? { duration: 0 }
      : { duration: 0.5, delay: arrived ? delay : 0, ease: EASE_OUT },
  });
  return (
    <Block>
      {(arrived) => (
        <>
          <DeskKa
            shown={deskShown}
            reduceMotion={reduceMotion}
            slotId="final"
            style={{ width: DESK_KA.w, height: DESK_KA.h, marginTop: -DESK_KA_HEADROOM }}
          />
          <TriggeredLine
            segments={[
              { text: t('landingV3.finalKa.pre') },
              { text: t('landingV3.finalKa.hl'), hl: true },
              { text: t('landingV3.finalKa.post') },
            ]}
            arrived={arrived}
            delay={0.35}
            reduceMotion={reduceMotion}
            className={cn('mt-[50px] w-[1030px] max-w-[85vw]', HEADLINE_CLASS)}
          />
          <motion.div {...rise(arrived, 0.9)} className="mt-[72px] w-[336px]">
            <GetStartedCta
              placement="final"
              size="xxl"
              variant={signUpVariant}
              fullWidth
              leftIcon={WandIcon}>
              {t('landingV3.cta')}
            </GetStartedCta>
          </motion.div>
          <motion.div {...rise(arrived, 1.05)} className="mt-4">
            <StoreRatingPills />
          </motion.div>
        </>
      )}
    </Block>
  );
}

/**
 * The ONE floating Professor Ka (the Rive icon), fixed to the viewport, and
 * how it travels.
 *
 * Ka is only ever in one of a few ANCHORS — never in between. Inside the
 * runway the stage machine (`stageAt`) decides: the centred pose while Ka
 * speaks its one line, bottom-right in the hero and through the whole card
 * stack (user 2026-09-30) — at his line it fades out with it while the deck
 * rises over it (`kaCoverAt`), so nothing sits behind the first card, and
 * fades back in in the corner. Below the runway the
 * agent chat holds Ka CENTRED over its line, inside its card (that one block
 * lies under Ka), until its slot has scrolled off the screen; the tiles
 * after it have no slot, so Ka goes back to his corner for them (by a fade: a glide would pass under them); from the map on
 * every block holds Ka CENTRED above its headline (user 2026-09-24: no more
 * trips to the corner), and Ka simply rides with that block, locked to its
 * slot as the page scrolls. It moves on to the next block's slot when that slot reaches the
 * reading band (a glide down the centre line, when both are on screen) or as
 * soon as its own block has scrolled away (it is then waiting in the next slot
 * and scrolls in with it). The LAST slot is the head of the closing section's
 * desk Ka (Rive): there this Ka fades out as that one fades in — never both —
 * and the desk Ka leaves with its block, so the FAQ and footer are Ka-free. Every change of anchor is a discrete
 * move — no position at which Ka rests halfway. While invisible (a fast
 * scroll, a hidden anchor, off screen) the springs jump, so every return is a
 * fade in place.
 */
function KaTravel({
  stage,
  stageSizeRef,
  scrollY,
  layoutTick,
  introDone,
  nudge,
  reduceMotion,
  onFinalChange,
  deskShown,
}: {
  stage: KaStage;
  stageSizeRef: RefObject<{ w: number; h: number }>;
  scrollY: MotionValue<number>;
  layoutTick: MotionValue<number>;
  introDone: boolean;
  /** The idle nudge is on: Ka sits bottom-centre with its "scroll down" pill. */
  nudge: boolean;
  reduceMotion: boolean;
  /** The closing block's slot holds Ka: the top bar's Get Started steps back. */
  onFinalChange: (final: boolean) => void;
  /** Set to 1 when the closing section's desk Ka takes over from this one. */
  deskShown: MotionValue<number>;
}) {
  const { t } = useTranslation();
  const finalRef = useRef(false);
  const spring = reduceMotion ? { stiffness: 10_000, damping: 500 } : KA_GLIDE;
  // Where Ka is = its ANCHOR's position, set directly (a slot anchor therefore
  // moves exactly with the page), plus a glide offset that springs back to
  // zero after each change of anchor. The glide runs in the anchor's own
  // frame, so gliding onto a block that is scrolling never chases it.
  const bx = useMotionValue(0);
  const by = useMotionValue(0);
  const ox = useMotionValue(0);
  const oy = useMotionValue(0);
  const scale = useMotionValue(KA_SMALL_SCALE);
  // Plain values written on every change of their parts — not a computed
  // transform, which can miss a same-value write and leave Ka at 0,0.
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const syncX = () => x.set(bx.get() + ox.get());
  const syncY = () => y.set(by.get() + oy.get());
  useMotionValueEvent(bx, 'change', syncX);
  useMotionValueEvent(ox, 'change', syncX);
  useMotionValueEvent(by, 'change', syncY);
  useMotionValueEvent(oy, 'change', syncY);
  /** 1 when the current anchor shows Ka, 0 for the hidden anchor — sprung. */
  const shownTarget = useMotionValue(1);
  const shown = useSpring(shownTarget, spring);
  // Presence: away on a hidden anchor, or while the deck rises over Ka's line
  // (a fast scroll no longer takes Ka — user 2026-09-24: only the top bar
  // gets out of the way). Ka fades and shrinks a little around its own
  // centre; its position keeps following the anchors underneath, so it
  // returns where the current section wants it. Scale never exceeds 1 — Ka's
  // canvas renders at its laid-out size and stays sharp only downscaled.
  // The cover takes Ka only at his line's spot (`atLine`): from the corner,
  // where he waits through the deck, nothing rises over him. Sprung, so the
  // switch between the two is a fade, never a pop.
  const atLineTarget = useMotionValue(1);
  const atLine = useSpring(atLineTarget, spring);
  const cover = useTransform(() => {
    // layoutTick: a resize changes s without a scroll event.
    layoutTick.get();
    const c = kaCoverAt(
      scrollY.get() / (stageSizeRef.current?.h || window.innerHeight || 1),
      reduceMotion,
    );
    return 1 - atLine.get() * (1 - c);
  });
  const presence = useTransform(() => shown.get() * cover.get());
  const presenceScale = useTransform(() => 1 - 0.15 * (1 - presence.get()));
  // Invisible, Ka is parked far off screen: an opacity-0 canvas still draws
  // every frame, but off screen the runtime's visibility check stops it (it
  // did the same for the old Lottie — through the whole deck that was half
  // the page's JavaScript). The box size never changes, and it is back in
  // place before the fade-in shows anything.
  const park = useTransform(presence, (p) => (p < 0.004 ? -100_000 : 0));

  /** The anchor Ka is on (null until the first placement). */
  const anchorRef = useRef<string | null>(null);
  /** A feature card is showing its own Ka (the oral exam): never two. */
  const kaInCard = useKaInCard();
  const glide = useRef<AnimationPlaybackControls[]>([]);
  /** Below the runway: the last slot to have reached the reading band (-1 =
   *  none), and whether Ka has floated on from it toward the next one. */
  const reachedRef = useRef(-1);
  const floatingRef = useRef(false);

  // Aim Ka at an anchor. The SAME anchor again just follows it (a slot moving
  // with the page). A NEW anchor glides from where Ka is only when Ka and the
  // anchor are BOTH wholly on screen — the one move a reader should see. In
  // every other case (a fast scroll, a hidden anchor, Ka or its target at or
  // past an edge) Ka arrives in place, and fades in there if that is on
  // screen: it never flies across the page, never pops.
  const place = (
    id: string,
    px: number,
    py: number,
    size: number,
    transition: typeof KA_GLIDE | typeof KA_FLOAT = KA_GLIDE,
    /** Arrive by a fade even where a glide could be seen: back to the
     *  corner after the agent chat, a glide would pass under the tiles. */
    fade = false,
    /** The slot's `data-lv3-ka` id: Ka has landed there once he arrives. */
    lands: string | null = null,
  ) => {
    if (id === anchorRef.current) {
      bx.set(px);
      by.set(py);
      return;
    }
    // Where Ka IS, read before the base moves: x/y follow bx/by synchronously.
    const fromX = x.get();
    const fromY = y.get();
    const fromS = scale.get();
    bx.set(px);
    by.set(py);
    const { w, h } = stageSizeRef.current ?? { w: 1512, h: 982 };
    const whollyOn = (ax: number, ay: number, s: number) =>
      ay >= 0 && ay + KA_BOX * s <= h && ax >= 0 && ax + KA_BOX * s <= w;
    const partlyOn = (ax: number, ay: number, s: number) =>
      ay < h && ay + KA_BOX * s > 0 && ax < w && ax + KA_BOX * s > 0;
    const target = size / KA_BOX;
    const first = anchorRef.current === null;
    anchorRef.current = id;
    atLineTarget.set(id === 'line' ? 1 : 0);
    glide.current.forEach((a) => a.stop());
    glide.current = [];
    setKaLanded(null);
    const visible = presence.get() >= 0.05;
    if (
      !first &&
      !fade &&
      !reduceMotion &&
      visible &&
      whollyOn(fromX, fromY, fromS) &&
      whollyOn(px, py, target)
    ) {
      ox.jump(fromX - px);
      oy.jump(fromY - py);
      // A float's length sets its time — about 600px a second, never under
      // 0.9s or over 1.9s — so a tall window's long float stays as gentle as
      // a short one's.
      const t =
        transition === KA_FLOAT
          ? {
              ...KA_FLOAT,
              duration: Math.min(
                1.9,
                Math.max(0.9, Math.hypot(fromX - px, fromY - py) / 600),
              ),
            }
          : transition;
      glide.current = [animate(ox, 0, t), animate(oy, 0, t), animate(scale, target, t)];
      // Landed once the glide is over — unless he has been sent on since.
      if (lands) {
        void glide.current[0].then(() => {
          if (anchorRef.current === id) setKaLanded(lands);
        });
      }
      return;
    }
    ox.jump(0);
    oy.jump(0);
    scale.jump(target);
    setKaLanded(lands);
    if (!first && visible && partlyOn(px, py, target)) {
      shown.jump(0);
      shownTarget.set(0);
      shownTarget.set(1);
    }
  };

  // The nudge's speech pill rides on Ka's sprung position (8px to the right,
  // 12px above Ka's top — Figma 65:23548) and shares its presence, so the
  // fast-scroll hide takes both.
  const nudgeTarget = useMotionValue(0);
  const nudgeShown = useSpring(nudgeTarget, spring);
  useEffect(() => {
    nudgeTarget.set(nudge ? 1 : 0);
  }, [nudge, nudgeTarget]);
  const toastX = useTransform(x, (v) => v + KA_SMALL + NUDGE_TOAST_GAP);
  const toastY = useTransform(y, (v) => v - NUDGE_TOAST_RAISE);
  const toastOpacity = useTransform(() => presence.get() * nudgeShown.get());
  const nudgeWords = useMemo(() => toWords([{ text: t('landingV3.nudge.text') }]), [t]);

  const update = () => {
    const { w, h } = stageSizeRef.current ?? { w: 1512, h: 982 };
    const sy = scrollY.get();
    const corner = { x: w - 58 - KA_SMALL, y: h - 58 - KA_SMALL, size: KA_SMALL };
    const centre = { x: w / 2 - KA_BOX / 2, y: 0.406 * h - KA_BOX / 2, size: KA_BOX };
    // Ka belongs to the page below from the moment the stage releases —
    // that page then scrolls up into view under the last card.
    const handoverPx = LANDING_TIMELINE.handover * h;

    if (sy < handoverPx) {
      deskShown.set(0);
      reachedRef.current = -1;
      floatingRef.current = false;
      if (finalRef.current) onFinalChange((finalRef.current = false));
      if (stage.kind === 'ka') {
        shownTarget.set(1);
        place('line', centre.x, centre.y, centre.size);
      } else if (sy < LANDING_TIMELINE.kaStarts[0] * h) {
        // The hero: Ka waits bottom-right for its first line — or, once the
        // reader has sat still long enough, bottom-centre with the nudge.
        shownTarget.set(1);
        if (nudge)
          place(
            'nudge',
            w / 2 - NUDGE_GROUP_W / 2,
            h - NUDGE_BOTTOM - KA_SMALL,
            KA_SMALL,
          );
        else place('corner', corner.x, corner.y, corner.size);
      } else {
        // Through the card stack: bottom-right (user 2026-09-30), as on the
        // phone. He arrives there by a fade once the deck has covered his
        // line — a glide would pass under the cards.
        // A card drawing its own Ka (the oral exam) has him meanwhile.
        shownTarget.set(kaInCard ? 0 : 1);
        place('corner', corner.x, corner.y, corner.size, KA_GLIDE, true);
      }
      return;
    }

    // Below the runway. Slots, in page order: the agent chat, the map, the
    // posts, the close. Ka rides each block's slot and floats on to wait for
    // the next (user 2026-09-24), back in his corner through the tiles
    // between the chat and the map (`kaSlotAt`, shared with the phone).
    const slotEls = [...document.querySelectorAll<HTMLElement>('[data-lv3-ka]')];
    const rects = slotEls.map((el) => el.getBoundingClientRect());
    const pick = kaSlotAt(
      rects,
      { top: 156, bottomInset: 156, height: h },
      { reached: reachedRef.current, floating: floatingRef.current },
      { parkGap: 1.5 * h },
    );
    reachedRef.current = pick.reached;
    floatingRef.current = pick.floating;

    if (pick.final !== finalRef.current) onFinalChange((finalRef.current = pick.final));
    // At the close the desk Ka stands in: this one fades out on the spot
    // where his head is, and the two are never shown together.
    deskShown.set(pick.final ? 1 : 0);

    if (pick.slot < 0) {
      // Before the agent chat, and through the tiles after it (they have no
      // slot): bottom-right, where he has waited through the deck — unless the
      // last card is still showing its own Ka.
      shownTarget.set(kaInCard ? 0 : 1);
      place('corner', corner.x, corner.y, corner.size, KA_GLIDE, pick.reached >= 0);
      return;
    }

    // The closing block's slot is the desk Ka's head: there this Ka hands
    // over and hides, and the desk Ka leaves with the block, so the FAQ and
    // footer are Ka-free. Ka is always on screen between the map and the
    // close, so a hand-off is always a float, never a cut or a flight.
    shownTarget.set(pick.final ? 0 : 1);
    place(
      `slot-${pick.slot}`,
      pick.x,
      pick.y,
      pick.size,
      KA_FLOAT,
      false,
      // Waiting for the next block is not landing on it.
      pick.floating ? null : (slotEls[pick.slot]?.dataset.lv3Ka ?? null),
    );
  };

  useMotionValueEvent(scrollY, 'change', update);
  useMotionValueEvent(layoutTick, 'change', update);
  useEffect(update, [stage, nudge, kaInCard]);

  return (
    <>
      {/* z-[3]: the bottom of the page's layers, over only the dot lattice.
          The sticky stage (z-[5]) sweeps the deck's cards over Ka as they are
          thrown, and the blocks below the runway (z-[4]) pass over Ka as it
          glides between their slots — a review card, a post, a button is
          never covered by Ka (user 2026-09-25). Its slots are empty boxes,
          so Ka at rest is never hidden. */}
      <motion.div
        style={{ x, y, scale, originX: 0, originY: 0, width: KA_BOX, height: KA_BOX }}
        className="pointer-events-none fixed left-0 top-0 z-[3]">
        {/* The presence fade: scale 1 and opaque at mount, only ever down. */}
        <motion.div
          style={{ opacity: presence, scale: presenceScale, x: park }}
          className="h-full w-full">
          <motion.div
            // A fade + rise, NOT a scale pop: Ka never scales UP (his canvas
            // renders at its laid-out size).
            initial={introDone || reduceMotion ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={
              introDone || reduceMotion
                ? { duration: 0 }
                : { delay: INTRO.ka, ...LAYOUT_SPRING }
            }
            className="relative h-full w-full">
            <FloatingKa reduceMotion={reduceMotion} />
          </motion.div>
        </motion.div>
      </motion.div>
      {/* The nudge's speech pill (Figma 65:23548): glass, 48px, tail corner
          towards Ka; the words stream in like Ka's line. */}
      <motion.div
        aria-hidden={!nudge}
        style={{ x: toastX, y: toastY, opacity: toastOpacity }}
        className={cn(
          'pointer-events-none fixed left-0 top-0 z-[3] flex h-12 items-center whitespace-nowrap rounded-[20px] rounded-bl-[10px] px-4 text-[14px] font-semibold leading-5 text-secondary-600',
          GLASS_PILL_CLASS,
        )}>
        {/* One inline run, not flex items: a word's leading space only
            survives inside a shared line box. */}
        <span>
          {nudgeWords.map((token, i) => (
            <KaWord
              key={i}
              text={token.word}
              leadingSpace={i > 0 && !token.attach}
              index={i}
              play={nudge}
              delay={0.25}
              reduceMotion={reduceMotion}
            />
          ))}
        </span>
      </motion.div>
    </>
  );
}

// Intro sequence timings (seconds), in the order the elements appear.
const INTRO = {
  pill: 0.1,
  headline: 0.5,
  headlineStagger: 0.055,
  cta: 1.3,
  chips: 1.65,
  chipStagger: 0.07,
  ka: 2.2,
} as const;
const INTRO_DONE_MS = 2800;
const IDLE_BADGES_MS = 3800;

// ── Headline reveal ──────────────────────────────────────────────────────────

/**
 * The headline is two lines in every language. Its first line never wraps;
 * where a translation runs wider than the box ("Jedyna AI jakiej
 * potrzebujesz" at 80px), the whole headline shrinks until it fits. Returns
 * the font-size factor (never above 1) for the lines.
 */
function useHeadlineFit(
  boxRef: RefObject<HTMLElement | null>,
  lineRef: RefObject<HTMLElement | null>,
): number {
  const [scale, setScale] = useState(1);
  const scaleRef = useRef(1);
  useLayoutEffect(() => {
    const box = boxRef.current;
    const line = lineRef.current;
    if (!box || !line) return;
    const measure = () => {
      const natural = line.offsetWidth / scaleRef.current;
      if (natural <= 0) return;
      const next = Math.min(1, box.clientWidth / natural);
      if (Math.abs(next - scaleRef.current) < 0.005) return;
      scaleRef.current = next;
      setScale(next);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    observer.observe(line);
    void document.fonts?.ready.then(measure);
    return () => observer.disconnect();
  }, [boxRef, lineRef]);
  return scale;
}

function RevealWords({
  segments,
  play,
  baseDelay,
  stagger,
  startIndex = 0,
}: {
  segments: { text: string; className?: string }[];
  play: boolean;
  baseDelay: number;
  stagger: number;
  startIndex?: number;
}) {
  let wordIndex = startIndex;
  return (
    <>
      {segments.map((seg, si) => {
        const words = seg.text.split(' ').filter(Boolean);
        return words.map((word, wi) => {
          const i = wordIndex++;
          const last = si === segments.length - 1 && wi === words.length - 1;
          // The separating space lives OUTSIDE the inline-block span — a
          // trailing space inside one is trimmed by whitespace collapsing.
          return (
            <span key={`${si}-${wi}`}>
              <motion.span
                className={cn('inline-block', seg.className)}
                initial={play ? { opacity: 0, y: 16 } : false}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  delay: baseDelay + i * stagger,
                  duration: 0.45,
                  ease: 'easeOut',
                }}>
                {word}
              </motion.span>
              {!last && ' '}
            </span>
          );
        });
      })}
    </>
  );
}

// ── Floating feature chip + badge group ──────────────────────────────────────

type ChipEnv = {
  scroll: LandingScroll;
  time: MotionValue<number>;
  uiTick: ReturnType<typeof useMotionValue<number>>;
  stageSizeRef: React.MutableRefObject<{ w: number; h: number }>;
  slotsRef: React.MutableRefObject<Record<string, { x: number; y: number }>>;
  hoverRef: React.MutableRefObject<string | null>;
};

/**
 * One feature: the chip and its badge inside a single transformed group.
 * Position, scale, rotation (including the float wobble) and drift are all
 * composed into that group's transform, mapped continuously from scroll —
 * scattered at `s = 0`, seated on its measured nav slot at `s = 1`.
 */
function FloatingFeature({
  feature,
  index,
  env,
  reduceMotion,
  introDone,
  idleBadges,
  hovered,
  onHoverChange,
  onClick,
}: {
  feature: LandingFeature;
  index: number;
  env: ChipEnv;
  reduceMotion: boolean;
  introDone: boolean;
  idleBadges: boolean;
  hovered: boolean;
  onHoverChange: (hovered: boolean) => void;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  const { scroll, time, uiTick, stageSizeRef, slotsRef, hoverRef } = env;
  const popIndex = Math.max(0, INTRO_POP_ORDER.indexOf(feature.id));

  // Gather progress for THIS chip: eased, with a tiny per-chip phase offset so
  // the flock gathers organically instead of moving as one rigid block.
  const gather = () => {
    const start = index * 0.02;
    // Progress through the HERO span (not a full viewport): the gather runs
    // in half a screen of scroll.
    const raw = clamp01(
      (clamp01(scroll.s.get() / LANDING_SPANS.hero) - start) / (1 - start),
    );
    if (reduceMotion) return raw > 0.5 ? 1 : 0;
    return easeInOutCubic(raw);
  };
  // Float drift, damped to zero as the chip docks.
  const drift = (speed: number, amp: number, phase: number, p: number) =>
    reduceMotion ? 0 : Math.sin(time.get() / speed + phase) * amp * (1 - p);

  const x = useTransform(() => {
    scroll.layoutTick.get();
    const p = gather();
    const heroX = (stageSizeRef.current.w * feature.hero.cx) / 100;
    const targetX = slotsRef.current[feature.id]?.x ?? heroX;
    return (
      heroX +
      (targetX - heroX) * p +
      drift(900 + index * 130, 4 + (index % 3) * 2, index * 1.7, p) -
      HERO_CHIP_SIZE / 2
    );
  });
  const y = useTransform(() => {
    scroll.layoutTick.get();
    const p = gather();
    const heroY = (stageSizeRef.current.h * feature.hero.cy) / 100;
    const targetY = slotsRef.current[feature.id]?.y ?? heroY;
    return (
      heroY +
      (targetY - heroY) * p +
      drift(1150 + index * 110, 6 + ((index + 1) % 3) * 2, index * 2.3, p) -
      HERO_CHIP_SIZE / 2
    );
  });
  const rotate = useTransform(() => {
    const p = gather();
    return feature.hero.rotate * (1 - p) + drift(1400 + index * 90, 1.6, index, p);
  });
  // 64px hero chip → the rail's chip size (its slots), purely via scale.
  const scale = useTransform(() => 1 - (1 - RAIL_CHIP_SIZE / HERO_CHIP_SIZE) * gather());
  const opacity = useTransform(() => {
    uiTick.get();
    const s = scroll.s.get();
    const p = gather();
    // Continuous version of the docked active/inactive dimming: full while
    // this feature's card is on top of the deck, 0.3 a full step away.
    const throwStart = LANDING_TIMELINE.cardThrowStart[index];
    // The last card is never thrown: lit from its arrival on.
    const offTop =
      index === LANDING_FEATURES.length - 1
        ? Math.max(0, throwStart - s)
        : Math.abs(s - (throwStart + LANDING_SPANS.card * 0.5));
    const dimmed = 1 - 0.7 * Math.min(1, offTop / (LANDING_SPANS.card * 1.1));
    const base = 1 - (1 - dimmed) * p;
    // Chips leave with the nav once the decks are done (closing sections).
    const navExit = 1 - clamp01((s - LANDING_TIMELINE.runwayEnd) / 0.45);
    if (hoverRef.current === feature.id) {
      return (p < 0.5 ? 0.85 : Math.max(base, 0.6)) * navExit;
    }
    return base * navExit;
  });
  // Badges melt away early in the gather; idle/hover fades stay on the inner
  // element (opacity only — the shared transform is untouched).
  const badgeGate = useTransform(() => clamp01(1 - gather() * 2.5));

  const badgeVisible = idleBadges || hovered;
  // A chip takes the pointer only while it can be seen: faded out after the
  // deck it would still catch clicks meant for the tiles under it.
  const pointerEvents = useTransform(opacity, (o) => (o < 0.05 ? 'none' : 'auto'));
  return (
    <motion.div
      style={{ x, y, scale, rotate, opacity, pointerEvents }}
      className="absolute left-0 top-0 z-[15]">
      <motion.div
        initial={introDone || reduceMotion ? false : { opacity: 0, scale: 0.4 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={
          introDone || reduceMotion
            ? { duration: 0 }
            : { delay: INTRO.chips + popIndex * INTRO.chipStagger, ...LAYOUT_SPRING }
        }>
        <div className="relative">
          <ChipFace
            feature={feature}
            onClick={onClick}
            onHoverChange={onHoverChange}
            label={t(feature.badgeKey)}
          />
          <motion.div
            style={{ opacity: badgeGate }}
            className="pointer-events-none absolute left-1/2 top-full mt-1.5">
            <div className="w-max -translate-x-1/2">
              <motion.span
                initial={false}
                animate={{ opacity: badgeVisible ? 1 : 0, y: badgeVisible ? 0 : -4 }}
                transition={{
                  duration: 0.3,
                  delay: badgeVisible && !hovered ? popIndex * 0.3 : 0,
                }}
                className={cn(
                  'flex h-7 items-center whitespace-nowrap rounded-lg border px-2 text-[12px] font-bold leading-4',
                  feature.badgeClass,
                )}>
                {t(feature.badgeKey)}
              </motion.span>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ── Desktop experience ───────────────────────────────────────────────────────

function DesktopLanding({
  reduceMotion,
  users,
}: {
  reduceMotion: boolean;
  users: number | null;
}) {
  const { t } = useTranslation();
  // A phone or tablet (an iPad gets this scene in landscape): its store is
  // the page's ONE primary — the floating button — and signing up on the web
  // steps back to secondary (user 2026-09-28).
  const storeDevice = useStoreDevice();
  const signUpVariant = storeDevice ? 'secondary' : 'primary';
  const [introDone, setIntroDone] = useState(reduceMotion);
  const [idleBadges, setIdleBadges] = useState(reduceMotion);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [interactive, setInteractive] = useState<'hero' | 'docked'>('hero');
  // ── Idle nudge (Figma 75:23556) ────────────────────────────────────────
  // Sitting on the hero for NUDGE_IDLE_MS after the intro, without scrolling,
  // brings Ka bottom-centre with "Psst... scroll down ↓". Once per session:
  // the first scroll — before or after it fires — retires it.
  const [nudge, setNudge] = useState(false);
  const nudgeDoneRef = useRef(false);
  /** The closing block holds Ka → the top bar's Get Started turns secondary. */
  const [finalActive, setFinalActive] = useState(false);
  // The closing section's desk Ka is up (it replaces the travelling Ka there).
  // A motion value, so the hand-over re-renders neither the page nor the
  // memoised blocks below the runway.
  const deskShown = useMotionValue(0);
  const retireNudge = () => {
    nudgeDoneRef.current = true;
    try {
      sessionStorage.setItem(NUDGE_SESSION_KEY, '1');
    } catch {
      // Storage may be unavailable (private mode): the ref still holds.
    }
  };
  /** Ka's current pose (discrete — see `stageAt`). */
  const [stage, setStage] = useState<KaStage>({ kind: 'corner' });
  /** Latched: interstitial k has been reached, so its line has streamed. */
  const [firedKa, setFiredKa] = useState<boolean[]>(() => KA_LINES.map(() => false));

  const stageRef = useRef<HTMLDivElement>(null);
  const stageSizeRef = useRef({ w: 1512, h: 982 });
  const slotsRef = useRef<Record<string, { x: number; y: number }>>({});
  const hoverRef = useRef<string | null>(null);
  hoverRef.current = hoveredId;

  const { scrollY } = useScroll();
  const layoutTick = useMotionValue(0);
  const uiTick = useMotionValue(0);
  // Scroll offset in viewport-heights — the page's single progress axis.
  const s = useTransform(() => {
    layoutTick.get();
    return scrollY.get() / (stageSizeRef.current.h || 1);
  });
  // Fast-scroll gate: 0 reading, 1 travelling — only the top bar reads it now
  // (Ka, its line and the rail stay put; user 2026-09-24).
  // The chips' float-drift clock. useTime ticked every frame for the page's
  // whole life and made all ten chips recompute and restyle on every frame,
  // docked or not; the drift only shows in the hero (it is damped to zero as a
  // chip docks), so the clock only runs there.
  const time = useMotionValue(0);
  useAnimationFrame((t) => {
    if (!reduceMotion && s.get() < LANDING_SPANS.hero) time.set(t);
  });
  const travelling = useScrollTravel(scrollY, reduceMotion);
  // A stop half-way through a card's throw (or the gather, or the deck's
  // rise) finishes it: the page always rests on one card.
  useDeckSnap(scrollY, stageSizeRef, reduceMotion);
  // Ka's line leaves with Ka as the deck rises over it (see `kaCoverAt`).
  const kaLineOpacity = useTransform(() => kaCoverAt(s.get(), reduceMotion));
  const scroll: LandingScroll = useMemo(
    () => ({ s, layoutTick, travelling }),
    [s, layoutTick, travelling],
  );

  // Measure the stage and the bottom-nav slots (they never translate, so
  // rects are valid at any scroll position). Re-measured on resize and once
  // fonts settle (labels change the pill widths).
  useLayoutEffect(() => {
    const measure = () => {
      const stage = stageRef.current;
      if (!stage) return;
      const stageRect = stage.getBoundingClientRect();
      stageSizeRef.current = { w: stageRect.width, h: stageRect.height };
      stage.querySelectorAll<HTMLElement>('[data-lv3-slot]').forEach((el) => {
        const r = el.getBoundingClientRect();
        slotsRef.current[el.dataset.lv3Slot as string] = {
          x: r.left - stageRect.left + r.width / 2,
          y: r.top - stageRect.top + r.height / 2,
        };
      });
      layoutTick.set(layoutTick.get() + 1);
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (stageRef.current) ro.observe(stageRef.current);
    document.fonts?.ready.then(measure).catch(() => undefined);
    return () => ro.disconnect();
  }, [layoutTick]);

  // Discrete stage + one-way "has been reached" latches. These are the ONLY
  // scroll-derived React state inside the runway: the interstitials animate
  // on their own timelines once their latch flips, so nothing re-runs or
  // reverses while you keep scrolling. (The blocks below the runway latch
  // themselves with `useInView`.)
  useMotionValueEvent(s, 'change', (v) => {
    if (v > 0.01) {
      if (!nudgeDoneRef.current) retireNudge();
      setNudge((on) => (on ? false : on));
    }
    setInteractive(v > 0.5 * LANDING_SPANS.hero ? 'docked' : 'hero');
    const next = stageAt(v);
    setStage((prev) => (sameStage(prev, next) ? prev : next));
    if (next.kind === 'ka' && !firedKa[next.k]) {
      setFiredKa((prev) => {
        if (prev[next.k]) return prev;
        const copy = [...prev];
        copy[next.k] = true;
        return copy;
      });
    }
  });

  useEffect(() => {
    if (reduceMotion) return;
    const doneTimer = window.setTimeout(() => setIntroDone(true), INTRO_DONE_MS);
    const badgeTimer = window.setTimeout(() => setIdleBadges(true), IDLE_BADGES_MS);
    return () => {
      window.clearTimeout(doneTimer);
      window.clearTimeout(badgeTimer);
    };
  }, [reduceMotion]);

  // Hover/UI state feeds computed motion values through refs; nudge them.
  useEffect(() => {
    uiTick.set(uiTick.get() + 1);
  }, [hoveredId, uiTick]);

  useEffect(() => {
    // No nudge where the store button floats: it would sit on the button, and
    // a thumb on a touch screen scrolls without being asked.
    if (!introDone || nudgeDoneRef.current || storeDevice) return;
    try {
      if (sessionStorage.getItem(NUDGE_SESSION_KEY)) {
        nudgeDoneRef.current = true;
        return;
      }
    } catch {
      // Unavailable storage: fall through, the ref alone gates the session.
    }
    let timer: number | undefined;
    const fire = () => {
      if (nudgeDoneRef.current) return;
      // A hidden tab is not a reader sitting still — wait for it to come back.
      if (document.visibilityState !== 'visible') {
        timer = window.setTimeout(fire, NUDGE_IDLE_MS);
        return;
      }
      retireNudge();
      if (scrollY.get() <= 8) setNudge(true);
    };
    timer = window.setTimeout(fire, NUDGE_IDLE_MS);
    return () => window.clearTimeout(timer);
  }, [introDone, scrollY, storeDevice]);

  // Stable, so the memoised page below the runway (it takes it for the
  // footer's Study Tools) does not re-render with every page state change.
  const jumpTo = useCallback(
    (i: number) =>
      window.scrollTo({
        // Land where card i sits readable on top of its deck.
        top:
          LANDING_TIMELINE.cardThrowStart[i] *
          (stageSizeRef.current.h || window.innerHeight),
        behavior: reduceMotion ? 'auto' : 'smooth',
      }),
    [reduceMotion],
  );
  const jumpToTop = () =>
    window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
  // Arriving from About's Study Tools menu or footer: straight onto the card.
  const openCard = useCallback(
    (i: number) =>
      window.scrollTo({
        top:
          LANDING_TIMELINE.cardThrowStart[i] *
          (stageSizeRef.current.h || window.innerHeight),
      }),
    [],
  );
  useLandingCardArrival(openCard);

  // Hero copy and brand pill hand-off, all mapped from scroll.
  // All as fractions of the hero span, so they keep pace with the chip gather.
  const heroP = () => clamp01(s.get() / LANDING_SPANS.hero);
  const heroOpacity = useTransform(() => 1 - clamp01(heroP() / 0.35));
  const heroY = useTransform(() => -80 * heroP());

  const pillProgress = useTransform(() => easeInOutCubic(clamp01(heroP() / 0.6)));
  const pillY = useTransform(() => {
    scroll.layoutTick.get();
    const heroTop = 0.12 * stageSizeRef.current.h;
    return (40 - heroTop) * pillProgress.get();
  });
  const pillOpacity = useTransform(() => 1 - clamp01((heroP() - 0.15) / 0.3));

  const headlineRef = useRef<HTMLHeadingElement>(null);
  const headlineLine1Ref = useRef<HTMLSpanElement>(null);
  const headlineScale = useHeadlineFit(headlineRef, headlineLine1Ref);
  const headlineLine1 = useMemo(
    () => [
      { text: t('landingV3.headline.pre') },
      { text: t('landingV3.headline.ai'), className: 'text-primary-600' },
      { text: t('landingV3.headline.post') },
    ],
    [t],
  );
  const line1Words = headlineLine1
    .flatMap((seg) => seg.text.split(' '))
    .filter(Boolean).length;

  const env: ChipEnv = { scroll, time, uiTick, stageSizeRef, slotsRef, hoverRef };

  return (
    // The app's dotted surface, painted by the page itself so it runs the
    // full 11-viewport runway (the Layout's copy only covers the first
    // viewport — its element is h-full).
    <div className="relative isolate w-full bg-background">
      <PageLattice />
      {/* The page's <main> landmark: the runway and the blocks below it (the
          footer and the fixed chrome sit outside). Unpositioned, so it makes
          no stacking context — the stage, Ka and the blocks layer as before. */}
      <main>
        {/* Scroll runway: hero + Ka interstitials + one step per card throw,
          then the stage releases and the rest of the page scrolls normally. */}
        <div style={{ height: `${(LANDING_TIMELINE.totalSteps + 1) * 100}vh` }}>
          {/* `overflow-clip`, not `-hidden`: hidden still makes the stage a
            programmatic scroll container, and a focus scroll (a tab in the
            leaderboard, a card in the review marquee's 6,500px track) dragged
            the whole stage 257px sideways. Clip cannot scroll. */}
          {/* z-[5]: the whole stage sits above the fixed Ka (z-[3]) and the
            blocks below the runway (z-[4]) so the deck's cards sweep over
            both. */}
          {/* pointer-events-none: the stage is transparent and, for the stretch
            where the page below is pulled up under the runway's tail, it sits
            OVER the first blocks — it swallowed every click on the tiles (the
            leaderboard toggle, the timer). Only its live parts take the
            pointer: the hero while it is up, the chips while visible, the
            front card's CTA and media. */}
          <div
            ref={stageRef}
            className="pointer-events-none sticky top-0 z-[5] h-screen overflow-clip">
            {/* Hero copy — fades/lifts continuously with scroll. */}
            <motion.div
              style={{ opacity: heroOpacity, y: heroY }}
              className={cn(
                'absolute inset-0 z-0',
                interactive === 'docked' ? 'pointer-events-none' : 'pointer-events-auto',
              )}>
              <h1
                ref={headlineRef}
                className="absolute left-1/2 top-1/2 w-[1030px] max-w-[85vw] -translate-x-1/2 -translate-y-1/2 text-center text-[64px] font-bold leading-none text-secondary-800 xl:text-[80px]">
                <span className="block" style={{ fontSize: `${headlineScale}em` }}>
                  <span ref={headlineLine1Ref} className="inline-block whitespace-nowrap">
                    <RevealWords
                      segments={headlineLine1}
                      play={!introDone && !reduceMotion}
                      baseDelay={INTRO.headline}
                      stagger={INTRO.headlineStagger}
                    />
                  </span>
                </span>{' '}
                {/* The space above: the lines are blocks on screen, but in
                    the text (search, AI answers, screen readers) they are one
                    sentence — "you need for studying", not "needfor". */}
                <span className="block" style={{ fontSize: `${headlineScale}em` }}>
                  <RevealWords
                    segments={[{ text: t('landingV3.headline.line2') }]}
                    play={!introDone && !reduceMotion}
                    baseDelay={INTRO.headline}
                    stagger={INTRO.headlineStagger}
                    startIndex={line1Words}
                  />
                </span>
              </h1>
              <motion.div
                initial={introDone || reduceMotion ? false : { opacity: 0, y: 36 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: introDone ? 0 : INTRO.cta, ...LAYOUT_SPRING }}
                className="absolute inset-x-0 top-[65.5%] flex flex-col items-center gap-3">
                <div className="w-[336px]">
                  <GetStartedCta
                    placement="hero"
                    size="xxl"
                    variant={signUpVariant}
                    fullWidth
                    leftIcon={WandIcon}>
                    {t('landingV3.cta')}
                  </GetStartedCta>
                </div>
                <TrustedPill users={users} />
              </motion.div>
            </motion.div>

            {/* Brand pill — rises toward the top bar and fades as it arrives. */}
            <div className="pointer-events-none absolute inset-x-0 top-[12%] z-20 flex justify-center">
              <motion.div
                style={{ y: pillY, opacity: pillOpacity, borderRadius: 24 }}
                className={cn('flex items-center gap-2 p-4', GLASS_PILL_CLASS)}>
                <motion.div
                  initial={introDone || reduceMotion ? false : { opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    delay: introDone ? 0 : INTRO.pill,
                    duration: 0.5,
                    ease: 'easeOut',
                  }}
                  className="flex items-center gap-2">
                  <LogoMark className="h-6 w-[22px]" flightTarget />
                  <FlashkaWordmark className="h-[21px] w-auto text-secondary-700" />
                </motion.div>
              </motion.div>
            </div>

            {/* Always-mounted chrome — every element scrubs with scroll. */}
            <FeatureCardDeck scroll={scroll} reduceMotion={reduceMotion} />
            <FeatureRail scroll={scroll} />

            {/* The ten chip+badge groups, gathering into the rail's slots. */}
            {LANDING_FEATURES.map((feature, i) => (
              <FloatingFeature
                key={feature.id}
                feature={feature}
                index={i}
                env={env}
                reduceMotion={reduceMotion}
                introDone={introDone}
                idleBadges={idleBadges}
                hovered={hoveredId === feature.id}
                onHoverChange={(h) => setHoveredId(h ? feature.id : null)}
                onClick={() => jumpTo(i)}
              />
            ))}

            {/* Mid-page interstitial lines — Ka + the streamed copy, no badge.
              Each streams once, the first time its interstitial is reached.
              z-[4], under the deck (z-[5] inside the stage): the line is
              Ka speaking, so the deck rises and passes over it as it does
              over Ka. */}
            <motion.div
              // The line is Ka speaking: it steps back with Ka as the first card
              // rises over it.
              style={{ opacity: kaLineOpacity }}
              className="pointer-events-none absolute inset-x-0 top-1/2 z-[4]">
              {KA_LINES.map((_, k) => (
                <KaLine
                  key={k}
                  lineIndex={k}
                  fired={firedKa[k]}
                  active={stage.kind === 'ka' && stage.k === k}
                  reduceMotion={reduceMotion}
                />
              ))}
            </motion.div>
          </div>
        </div>
        <BelowRunway
          reduceMotion={reduceMotion}
          users={users}
          deskShown={deskShown}
          signUpVariant={signUpVariant}
        />
      </main>
      {/* z-[4]: over the fixed Ka (z-[3]), like the blocks. */}
      <div className="relative z-[4]">
        <LandingFooter onJumpTo={jumpTo} />
      </div>

      {/* Fixed chrome, outside the stage so it outlives the runway. */}
      <TopBar
        scroll={scroll}
        interactive={interactive === 'docked'}
        final={finalActive}
        onJumpTo={jumpTo}
        onLogoClick={jumpToTop}
      />
      <KaTravel
        stage={stage}
        stageSizeRef={stageSizeRef}
        scrollY={scrollY}
        layoutTick={layoutTick}
        introDone={introDone}
        nudge={nudge}
        reduceMotion={reduceMotion}
        onFinalChange={setFinalActive}
        deskShown={deskShown}
      />
      {storeDevice && <FloatingGetApp store={storeDevice} />}
    </div>
  );
}

/**
 * Everything below the runway, as ONE memoised subtree: the blocks (the
 * agent chat, tiles, map, posts, the close, FAQ) and the footer. Its props change only with the
 * live user count, so page-level state (the Ka stage, chip hovers, the nudge,
 * the top bar's final state) no longer re-renders the dot map's thousand
 * dots, the tiles and the carousels.
 */
const BelowRunway = memo(function BelowRunway({
  reduceMotion,
  users,
  deskShown,
  signUpVariant,
}: {
  reduceMotion: boolean;
  users: number | null;
  /** 1 while the closing section's desk Ka stands in for the travelling one. */
  deskShown: MotionValue<number>;
  signUpVariant: 'primary' | 'secondary';
}) {
  return (
    <>
      {/* The agent chat (Figma 119:8431) first, UNDER the fixed Ka (z-[2]
          against his z-[3]): he floats into its card and speaks there. It
          follows the released stage in plain flow — never pulled up behind
          the last card (user 2026-10-09): 107px + the deck's 93px under the
          card = the page's 200px rhythm. */}
      <div
        {...sectionAnchor('agent')}
        className="relative z-[2] flex w-full flex-col items-center px-6 pt-[107px]">
        <AgentChat reduceMotion={reduceMotion} />
      </div>
      {/* z-[4]: every other block sits over the fixed Ka (z-[3]), which
          glides between them underneath; the stage (z-[5]) stays on top.
          (The footer, outside <main>, has its own z-[4] box.) The tiles sit
          50px under the agent chat (Figma 119:8431, user 2026-09-30), then
          the blocks keep a 200px rhythm (Figma 118:1343): tiles → map →
          posts → closing CTA → FAQ, ending 86px above the footer. */}
      <div className="relative z-[4]">
        <div className="flex w-full flex-col items-center gap-[200px] px-6 pb-[86px] pt-[50px]">
          <TilesBlock />
          <ProofSection reduceMotion={reduceMotion} users={users} />
          <PostsSection reduceMotion={reduceMotion} />
          <FinalKaSection
            reduceMotion={reduceMotion}
            deskShown={deskShown}
            signUpVariant={signUpVariant}
          />
          <FaqSection />
        </div>
      </div>
    </>
  );
});

// ── Page ─────────────────────────────────────────────────────────────────────

export default function LandingV3Page() {
  const isDesktop = useIsDesktop();
  const reduceMotion = useReducedMotion() ?? false;
  const { i18n } = useTranslation();
  // One fetch for the whole page: the trusted pill and the social-proof
  // headline are the same total at two precisions, and the desktop tree shows
  // both.
  const users = useLiveUserCount();
  // Also tags a visitor who reached this page inside the app (the blog, a
  // footer link), not only one who opened on it — see `tagLandingVersion`.
  useEffect(() => tagLandingVersion(), []);
  // `/home/ai-agent` and the other section URLs open on their section.
  useLandingSectionArrival();

  // The live leaderboard is fetched as soon as the page goes idle — after the
  // hero, long before anyone scrolls to its tile — so the tile never pulses.
  const queryClient = useQueryClient();
  useEffect(() => {
    const run = () => void prefetchLandingLeaderboard(queryClient, i18n.language);
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(run, { timeout: 3000 });
      return () => window.cancelIdleCallback(id);
    }
    const id = window.setTimeout(run, 1500);
    return () => window.clearTimeout(id);
  }, [queryClient, i18n.language]);

  // The bare `/` route renders outside `LangBoundary` (see App.tsx), so nothing
  // else keeps `<html lang>`/`dir` in step with the language a returning
  // visitor restored from the i18next cache. Idempotent on `/<lang>/home`,
  // where LangBoundary sets the same values.
  useEffect(() => {
    const lang = (i18n.language || 'en').split('-')[0];
    document.documentElement.lang = lang;
    document.documentElement.dir = (RTL_LANGS as readonly string[]).includes(lang)
      ? 'rtl'
      : 'ltr';
  }, [i18n.language]);

  return isDesktop ? (
    <DesktopLanding reduceMotion={reduceMotion} users={users} />
  ) : (
    <PhoneLanding reduceMotion={reduceMotion} users={users} />
  );
}
